import React, { useEffect, useRef, useState } from "react"
import ContentLoader from "react-content-loader"
import { styled } from "@mui/material/styles"
import { AppTheme } from "types/general.types"

interface LazyImageProps {
  src: string | null;
  alt: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  placeHolderStyle?: React.CSSProperties;
  theme?: AppTheme;
}

export default function LazyImage(props: LazyImageProps) {
  const {
    src = "",
    alt,
    placeholder = "/static/images/placeholder/image-placeholder.png",
    className,
    style,
    placeHolderStyle = { width: 24, height: 24 },
    theme = "light"
  } = props

  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined)
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(false)

  const imageRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    if (!src) {
      handleError()
      return
    }

    const imgElement = imageRef.current
    if (!imgElement) return

    const observer = initializeImageObserver(imgElement, src)

    return () => {
      observer.disconnect()
    }
  }, [src])

  function initializeImageObserver(imgElement: HTMLImageElement, src: string) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setImageSrc(src)
          setShowPlaceholder(false)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(imgElement)
    return observer
  }

  function handleLoad() {
    setIsLoaded(true)
  }

  function handleError() {
    if (src && !imageSrc) return
    setImageSrc(placeholder)
    setShowPlaceholder(true)
  }

  return (
    <>
      {!isLoaded && <LoaderWrapper>
          <ContentLoader
              foregroundColor={theme === "light" ? "#eee" : "#070707"}
              backgroundColor={theme === "light" ? "#f5f6f7" : "#000"}
          >
              <rect x="0" y="0" width="100%" height="100%"/>
          </ContentLoader>
      </LoaderWrapper>}
      <img
        key={imageSrc}
        ref={imageRef}
        src={imageSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          objectFit: "cover",
          ...style,
          ...(showPlaceholder ? placeHolderStyle : {}),
          opacity: isLoaded ? 1 : 0,
          transition: "opacity 0.3s ease"
        }}
        className={className}
      />
    </>
  )
}

const LoaderWrapper = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",

  "& svg": {
    width: "100% !important",
    height: "100% !important",
    stroke: "transparent !important"
  }
}))
